import { Viewer as PSV } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';
import { VIEWER_DEFAULTS } from '@/core/config';
import { mediaUrl } from '@/core/tour-loader';

// Eingangsdaten fuer einen Pano-Wechsel. Bewusst nur das, was der Viewer
// wirklich braucht — der Rest des Room-Objekts (Tags, Description) ist
// Sache des Overlays, nicht des Viewers.
export interface ViewerLoadOptions {
  pano: string;
  yaw: number;
  pitch?: number;
  zoom?: number;
  transition?: boolean;
}

export interface ViewerPosition {
  yaw: number;
  pitch: number;
}

export class TourViewer {
  private psv: PSV;

  constructor(container: HTMLElement, initial: ViewerLoadOptions) {
    this.psv = new PSV({
      container,
      panorama: mediaUrl(initial.pano),
      navbar: VIEWER_DEFAULTS.navbar,
      defaultYaw: initial.yaw,
      defaultPitch: initial.pitch ?? VIEWER_DEFAULTS.defaultPitch,
      defaultZoomLvl: initial.zoom ?? VIEWER_DEFAULTS.defaultZoom,
      // Loader-Farbe wird via CSS (.psv-loader) gesetzt — PSV bietet
      // dafuer keine offizielle Konfig-Option, nur Text/Bild.
      loadingTxt: 'Lade …',
    });
  }

  // Wechselt das Panorama mit Crossfade. Initiale Blickrichtung wird neu
  // gesetzt; ohne diesen Reset bleibt PSV bei der alten Yaw/Pitch stehen.
  async loadPanorama(opts: ViewerLoadOptions): Promise<void> {
    await this.psv.setPanorama(mediaUrl(opts.pano), {
      transition:
        opts.transition === false
          ? false
          : { speed: VIEWER_DEFAULTS.transitionMs, rotation: false, effect: 'fade' },
      showLoader: true,
      position: {
        yaw: opts.yaw,
        pitch: opts.pitch ?? VIEWER_DEFAULTS.defaultPitch,
      },
      zoom: opts.zoom ?? VIEWER_DEFAULTS.defaultZoom,
    });
  }

  getPosition(): ViewerPosition {
    const p = this.psv.getPosition();
    return { yaw: p.yaw, pitch: p.pitch };
  }

  // Fuer den Coords-Ticker: ein Listener pro Frame ist zu teuer,
  // PSV emittiert 'position-updated' nur bei Aenderung.
  onPositionChange(cb: (pos: ViewerPosition) => void): () => void {
    const handler = (): void => cb(this.getPosition());
    this.psv.addEventListener('position-updated', handler);
    return () => this.psv.removeEventListener('position-updated', handler);
  }

  destroy(): void {
    this.psv.destroy();
  }
}
